<script>
  import UploadDocumentDialog from '../../components/UploadDocumentDialog.vue';
  import LoadingState from '../../../../../components/widgets/LoadingState.vue';
  import { mapState, mapGetters, mapActions } from 'vuex';
  import { useAlert } from 'dashboard/composables';

  export default {
    name: 'view_knowledge_documents',
    components: {
      UploadDocumentDialog,
      LoadingState
    },
    data(){
      return {
          isLoading: true,
          isUpload: false,
          datasetId: this.$route.params.id,
          isUploading: false,
          isDeleting: false,
          isConfirmingDelete: false,
          deleteId: null,
          searchQuery: '',
          sortKey: 'uploadTime',
          sortDirection: 'desc',
          viewMode: 'table', // 'table' or 'card',
      }
    },
    computed: {
      ...mapState('knowledge', ['tableBody', 'knowledgeList', 'knowledgeDetails']),
      ...mapGetters({
        accountId: 'getCurrentAccountId',
        pagination: 'knowledge/getPagination',
      }),
      ...mapGetters('stripe', ['getWordsLimit']),
      tableHeaders() {
        return [
          this.$t('KNOWLEDGE.VIEW_DOCUMENTS.TABLE_HEADERS.INDEX'),
          this.$t('KNOWLEDGE.VIEW_DOCUMENTS.TABLE_HEADERS.FILENAME'),
          this.$t('KNOWLEDGE.VIEW_DOCUMENTS.TABLE_HEADERS.SOURCE'),
          this.$t('KNOWLEDGE.VIEW_DOCUMENTS.TABLE_HEADERS.WORDS'),
          this.$t('KNOWLEDGE.VIEW_DOCUMENTS.TABLE_HEADERS.RETRIEVAL_COUNT'),
          this.$t('KNOWLEDGE.VIEW_DOCUMENTS.TABLE_HEADERS.UPLOAD_TIME'),
          this.$t('KNOWLEDGE.VIEW_DOCUMENTS.TABLE_HEADERS.STATUS'),
          this.$t('KNOWLEDGE.VIEW_DOCUMENTS.TABLE_HEADERS.ACTION'),
        ];
      },
      currentKnowledgeList() {
        return this.knowledgeList.find(knowledge => knowledge.id === this.datasetId) || {};
      },
      isLightragEngine() {
        return this.knowledgeDetails.engine === 'lightrag';
      },
      // Check if word limit is reached or exceeded
      isWordLimitExceeded() {
        const wordCount = this.currentKnowledgeList.true_word_count || 0;
        const wordLimit = this.getWordsLimit || 100000; // Default fallback value
        return wordCount >= wordLimit;
      },
      // Filtered and sorted documents
      processedDocuments() {
        // First filter documents based on search query
        let documents = this.tableBody || [];

        if (this.searchQuery) {
          const query = this.searchQuery.toLowerCase();
          documents = documents.filter(doc => {
            const fileName = doc.fileName ? doc.fileName.toLowerCase() : '';
            const status = doc.status ? doc.status.toLowerCase() : '';
            return fileName.includes(query) || status.includes(query);
          });
        }

        return documents;
      },
      // Check if there are any documents
      hasDocuments() {
        return this.tableBody && this.tableBody.length > 0;
      },
      // Generate an array of page numbers to display
      pageNumbers() {
        const { currentPage, totalPages } = this.pagination;
        const maxPagesToShow = 5; // Show at most 5 page numbers

        if (totalPages <= maxPagesToShow) {
          // If we have fewer pages than the max, show all pages
          return Array.from({ length: totalPages }, (_, i) => i + 1);
        } else {
          // Always include the first, last, current, and pages adjacent to current
          const pages = [1];

          // Calculate start and end of the page range around the current page
          let rangeStart = Math.max(2, currentPage - 1);
          let rangeEnd = Math.min(totalPages - 1, currentPage + 1);

          // Adjust the range to always show 3 pages if possible
          if (rangeEnd - rangeStart + 1 < 3) {
            if (rangeStart === 2) {
              rangeEnd = Math.min(totalPages - 1, rangeStart + 2);
            } else if (rangeEnd === totalPages - 1) {
              rangeStart = Math.max(2, rangeEnd - 2);
            }
          }

          // Add ellipsis if needed before the range
          if (rangeStart > 2) {
            pages.push('...');
          }

          // Add the range of pages
          for (let i = rangeStart; i <= rangeEnd; i++) {
            pages.push(i);
          }

          // Add ellipsis if needed after the range
          if (rangeEnd < totalPages - 1) {
            pages.push('...');
          }

          // Add the last page
          if (totalPages > 1) {
            pages.push(totalPages);
          }

          return pages;
        }
      }
    },
    methods: {
      ...mapActions('knowledge', ['handleRemoveFile', 'fetchDocuments', 'handleDeleteDocument', 'changePage', 'fetchDatasetDetails']),
      ...mapActions('stripe', ['fetchSubscription']),
      // Toggle view mode between table and card
      toggleViewMode() {
        this.viewMode = this.viewMode === 'table' ? 'card' : 'table';
      },
      // Get appropriate icon for document type
      getFileIcon(filename) {
        if (!filename) return 'document';

        const extension = filename.split('.').pop().toLowerCase();

        if (['doc', 'docx'].includes(extension)) return 'document-text';
        if (['xls', 'xlsx'].includes(extension)) return 'document-table';
        if (['ppt', 'pptx'].includes(extension)) return 'slide-text';
        if (['pdf'].includes(extension)) return 'document-pdf';
        if (['txt'].includes(extension)) return 'document-text';
        if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'].includes(extension)) return 'image';

        return 'document';
      },
      // Get appropriate status text
      getStatusText(status) {
        const statusMap = {
          'available': 'Available',
          'indexing': 'Indexing in progress',
          'unavailable': 'Unavailable',
          'disabled': 'Disabled',
          'queuing': 'In queue',
          'error': 'Error processing'
        };

        return statusMap[status] || status;
      },

      // Row fields rendered as table cells. `error` only feeds the status
      // badge tooltip, so it has no column of its own.
      tableCells(row) {
        const { error, ...cells } = row;
        return cells;
      },

      // Format date for better readability
      formatDate(dateString) {
        if (!dateString) return '';

        const date = new Date(dateString);
        return new Intl.DateTimeFormat('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }).format(date);
      },
      toTitleCase(text) {
        return text
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      },
      // Handle document cancel
      handleCancel(){
        this.handleRemoveFile();
        this.isUpload = false;
      },
      // Refresh documents list
      async handleRefresh(){
        this.isLoading = true;
        try {
          await this.fetchDocuments(this.datasetId);
          await this.fetchDatasetDetails(this.datasetId);
        } catch(err) {
          console.error(err);
        } finally {
          this.isLoading = false;
        }
      },
      // Load a (possibly new) dataset's documents, resetting pagination first
      // so a previously-viewed dataset's page/total doesn't leak into this one
      async loadDataset(){
        this.$store.commit('knowledge/resetPagination');
        await this.handleRefresh();
      },
      // Handle document deletion
      handleDeleteLocal(id){
        this.isDeleting = true;
        this.deleteId = id;
      },
      // Cancel deletion
      cancelDelete(){
        this.isDeleting = false;
        this.deleteId = null;
      },
      // Confirm document deletion
      async handleConfirmDelete(){
        this.isConfirmingDelete = true;
        try{
          await this.handleDeleteDocument({datasetId: this.datasetId, document_id: this.deleteId,});
          this.cancelDelete();
          await this.handleRefresh();
        }catch(err){
          console.error(err);
        }finally{
          this.isConfirmingDelete = false;
        }
      },
      // Go to specific page in pagination
      async goToPage(page) {
        if (typeof page === 'number' && page !== this.pagination.currentPage) {
          this.isLoading = true;
          try {
            await this.changePage({ page, datasetId: this.datasetId });
          } catch (err) {
            console.error(err);
          } finally {
            this.isLoading = false;
          }
        }
      },
      // Navigate to view document page
      viewDocument(document) {
        if(document.status !== 'available' && document.status !== 'disabled'){
          useAlert(`${this.$t('KNOWLEDGE.VIEW_DOCUMENTS.UNABLE_VIEW_CHUNKS_MESSAGE')}"${document.status}".`);
          return;
        }

        this.$store.commit('knowledge/setCurrentDocument', document);

        this.$router.push({
          name: 'view_document',
          params: {
            documentId: document.id,
            document: document,
          }
        });
      }
    },
    watch: {
      '$route.params.id'(newId){
        this.datasetId = newId;
        this.loadDataset();
      }
    },
    mounted(){
      this.loadDataset();
      this.fetchSubscription({ id: this.accountId });
    }
  }
</script>

<template>
  <div class="p-4 md:p-8 flex flex-1 h-full flex-col m-0 overflow-auto bg-slate-50 dark:bg-slate-900 transition-colors duration-200">
    <!-- Actions -->
    <header class="mb-6">
      <!-- Action buttons and search -->
      <div class="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
        <!-- Search bar -->
        <div class="relative flex-grow max-w-md w-full">
          <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none h-10">
            <fluent-icon icon="search" class="h-5 w-5 text-slate-400" aria-hidden="true" />
          </div>
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="$t('KNOWLEDGE.VIEW_DOCUMENTS.SEARCH_DOCUMENTS')"
            class="pl-10 pr-4 py-2 w-full border border-slate-300 dark:border-slate-700 rounded-md
            text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800 focus:ring-2 focus:ring-woot-500
            focus:border-woot-500 transition-colors"
            aria-label="Search documents"
            data-testid="document_search_input"
          />
          <button
            v-if="searchQuery"
            @click="searchQuery = ''"
            class="absolute inset-y-0 right-0 pr-3 flex items-center"
            data-testid="document_search_clear_button"
          >
            <fluent-icon icon="dismiss" class="h-5 w-5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300" aria-label="Clear search" />
          </button>
        </div>

        <!-- Action buttons -->
        <div class="flex gap-2 w-full md:w-auto">
          <woot-button
            variant="smooth"
            color-scheme="primary"
            icon="arrow-clockwise"
            @click="handleRefresh()"
            aria-label="Refresh documents"
            data-testid="document_refresh_button"
          />
          <woot-button
            variant="smooth"
            color-scheme="primary"
            :icon="viewMode === 'table' ? 'table' : 'grid'"
            @click="toggleViewMode"
            aria-label="Toggle view mode"
            data-testid="document_view_toggle_button"
          >
            {{ viewMode === 'table' ? $t('KNOWLEDGE.VIEW_DOCUMENTS.TABLE_VIEW') : $t('KNOWLEDGE.VIEW_DOCUMENTS.CARD_VIEW') }}
          </woot-button>
          <div class="relative group">
            <woot-button
              variant="smooth"
              color-scheme="primary"
              icon="add"
              class="ml-2"
              :disabled="isWordLimitExceeded"
              :title="$t('KNOWLEDGE.VIEW_DOCUMENTS.ADD_DOCUMENT_TITLE')"
              @click="isUpload = !isUpload"
              data-testid="document_add_button"
            >
              {{ $t('KNOWLEDGE.VIEW_DOCUMENTS.ADD_DOCUMENT_BUTTON') }}
            </woot-button>

            <!-- Tooltip -->
            <div
              v-if="isWordLimitExceeded"
              class="pointer-events-none absolute bottom-full right-0 mb-2
                    opacity-0 group-hover:opacity-100 transition
                    bg-red-50 dark:bg-red-900/30
                    border border-red-200 dark:border-red-800
                    rounded-md p-2 shadow-md text-xs
                    text-red-600 dark:text-red-400
                    max-w-xs z-50 whitespace-normal"
              v-html="$t('KNOWLEDGE.VIEW_DOCUMENTS.WORD_LIMIT_EXCEED_WARNING')"
              data-testid="document_word_limit_warning"
            />
          </div>
        </div>
      </div>
    </header>

    <!-- Upload document dialog -->
    <UploadDocumentDialog
      v-if="isUpload"
      :datasetId="datasetId"
      :existingWordCount="knowledgeDetails.true_word_count"
      :engine="isLightragEngine ? 'lightrag' : 'dify'"
      @cancel="handleCancel"
      @success="handleCancel(); handleRefresh()"
    />

    <!-- Delete confirmation dialog -->
    <div v-if="isDeleting" class="z-40 fixed top-0 left-0 h-full w-full bg-slate-900/50 backdrop-blur-sm flex items-center justify-center" @click="cancelDelete()" data-testid="document_delete_backdrop"></div>
    <div
      v-if="isDeleting"
      class="z-50 w-full max-w-md fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 p-6 rounded-lg bg-white dark:bg-slate-800 shadow-xl"
    >
      <h3 class="text-lg font-medium text-slate-900 dark:text-slate-100 mb-4">{{ $t('KNOWLEDGE.VIEW_DOCUMENTS.DELETE_TITLE') }}</h3>
      <p class="text-slate-600 dark:text-slate-400 mb-6">{{ $t('KNOWLEDGE.VIEW_DOCUMENTS.DELETE_DESCRIPTION') }}</p>
      <div class="flex justify-end gap-3">
        <woot-button
          variant="smooth"
          color-scheme="secondary"
          :is-disabled="isConfirmingDelete"
          @click="cancelDelete()"
          data-testid="document_delete_cancel_button"
        >
          {{ $t('KNOWLEDGE.CANCEL_BUTTON') }}
        </woot-button>
        <woot-button
          variant="smooth"
          color-scheme="alert"
          icon="delete"
          :is-loading="isConfirmingDelete"
          @click="handleConfirmDelete"
          data-testid="document_delete_confirm_button"
        >
          {{
            isConfirmingDelete
              ? $t('KNOWLEDGE.DELETING_BUTTON')
              : $t('KNOWLEDGE.DELETE_BUTTON')
          }}
        </woot-button>
      </div>
    </div>

    <!--
      Single loading gate: the dataset details, pagination, and document list
      only ever appear together, all at once, instead of as separately-toggled
      regions — avoids any chance of one piece rendering before another.
    -->
    <div v-if="isLoading" class="flex-1 flex items-center justify-center py-20" data-testid="document_loading_state">
      <LoadingState :message="$t('KNOWLEDGE.VIEW_DOCUMENTS.LOADING_STATE')" />
    </div>

    <template v-else>
      <!-- Pagination controls -->
      <div v-if="pagination.totalPages > 0" class="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div class="text-sm text-slate-600 dark:text-slate-400" data-testid="document_showing_count">
          {{ $t('KNOWLEDGE.VIEW_DOCUMENTS.SHOWING') }} <span class="font-medium text-slate-800 dark:text-slate-200">{{ processedDocuments.length }}</span>
          {{ processedDocuments.length === 1 ? $t('KNOWLEDGE.DOCUMENT_COUNT') :  $t('KNOWLEDGE.DOCUMENT_COUNT_PLURAL') }}
          {{ searchQuery ? `${$t('KNOWLEDGE.VIEW_DOCUMENTS.MATCHING')} "${searchQuery}"` : '' }}
        </div>

        <!-- Pagination navigation -->
        <div class="flex items-center space-x-2">
          <woot-button
            variant="smooth"
            color-scheme="secondary"
            size="small"
            :disabled="pagination.currentPage === 1"
            @click="goToPage(pagination.currentPage - 1)"
            aria-label="Previous page"
            data-testid="document_pagination_prev"
          >
            <fluent-icon icon="chevron-left" class="h-4 w-4 mr-1" />
            {{ $t('KNOWLEDGE.VIEW_DOCUMENTS.PREVIOUS_BUTTON') }}
          </woot-button>

          <div class="hidden md:flex items-center space-x-1">
            <button
              v-for="page in pageNumbers"
              :key="page"
              @click="typeof page === 'number' ? goToPage(page) : null"
              :class="{
                'w-8 h-8 flex items-center justify-center rounded-md text-sm font-medium transition-colors': true,
                'bg-woot-500 text-white': page === pagination.currentPage,
                'bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700': page !== pagination.currentPage && page !== '...',
                'text-slate-400 dark:text-slate-500 cursor-default': page === '...'
              }"
              :disabled="page === '...'"
              :aria-current="page === pagination.currentPage ? 'page' : null"
              data-testid="document_pagination_page"
              :data-page="page"
            >
              {{ page }}
            </button>
          </div>

          <span class="text-sm text-slate-600 dark:text-slate-400 md:hidden">
            {{ $t('KNOWLEDGE.VIEW_DOCUMENTS.PAGINATION_INFO', { current: pagination.currentPage, total: pagination.totalPages }) }}
          </span>

          <woot-button
            variant="smooth"
            color-scheme="secondary"
            size="small"
            :disabled="pagination.currentPage === pagination.totalPages"
            @click="goToPage(pagination.currentPage + 1)"
            aria-label="Next page"
            data-testid="document_pagination_next"
          >
            {{ $t('KNOWLEDGE.VIEW_DOCUMENTS.NEXT_BUTTON') }}
            <fluent-icon icon="chevron-right" class="h-4 w-4 ml-1" />
          </woot-button>
        </div>
      </div>

      <!-- No documents empty state -->
      <div
        v-if="!hasDocuments || (processedDocuments.length === 0 && searchQuery)"
        class="flex flex-col items-center justify-center py-16 flex-1"
        data-testid="document_empty_state"
      >
        <div
          class="bg-slate-100 dark:bg-slate-800 rounded-full p-6 mb-4 text-slate-400 dark:text-slate-500"
        >
          <fluent-icon
            :icon="searchQuery ? 'search' : 'document'"
            class="h-12 w-12"
          />
        </div>
        <h3 class="text-xl font-medium text-slate-800 dark:text-slate-200 mb-2">
          {{ searchQuery ? $t('KNOWLEDGE.VIEW_DOCUMENTS.NO_MATCH_TITLE') : $t('KNOWLEDGE.VIEW_DOCUMENTS.EMPTY_STATE_TITLE') }}
        </h3>
        <p class="text-slate-600 dark:text-slate-400 text-center max-w-md mb-6">
          {{ searchQuery
            ? $t('KNOWLEDGE.VIEW_DOCUMENTS.NO_MATCH_DESCRIPTION', { query: searchQuery })
            : $t('KNOWLEDGE.VIEW_DOCUMENTS.EMPTY_STATE_DESCRIPTION')
          }}
        </p>
        <div class="flex flex-col items-center">
          <div class="flex gap-4">
            <woot-button
              v-if="searchQuery"
              variant="smooth"
              color-scheme="secondary"
              @click="searchQuery = ''"
              data-testid="document_empty_clear_search_button"
            >
              {{ $t('KNOWLEDGE.CLEAR_SEARCH_BUTTON') }}
            </woot-button>
            <woot-button
              variant="smooth"
              color-scheme="primary"
              icon="add"
              @click="isUpload = !isUpload"
              :disabled="isWordLimitExceeded"
              :title="isWordLimitExceeded ? $t('KNOWLEDGE.VIEW_DOCUMENTS.WORD_LIMIT_EXCEED_TITLE') : $t('KNOWLEDGE.VIEW_DOCUMENTS.ADD_DOCUMENT_TITLE')"
              data-testid="document_empty_state_add_button"
            >
              {{ searchQuery ? $t('KNOWLEDGE.VIEW_DOCUMENTS.UPLOAD_DOCUMENT') : $t('KNOWLEDGE.VIEW_DOCUMENTS.ADD_FIRST_DOCUMENT') }}
            </woot-button>
          </div>
          <!-- Word Limit Exceeded Warning in Empty State -->
          <div
            v-if="isWordLimitExceeded"
            class="mt-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md p-3 shadow-sm text-sm text-red-600 dark:text-red-400 max-w-md"
            v-html="$t('KNOWLEDGE.VIEW_DOCUMENTS.WORD_LIMIT_EXCEED_WARNING')"
          >
          </div>
        </div>
      </div>

      <!-- Document table view -->
      <div v-if="hasDocuments && processedDocuments.length > 0" class="flex-1">
        <!-- Table view -->
        <div v-if="viewMode === 'table'" class="overflow-x-auto bg-white dark:bg-slate-800 rounded-lg shadow-sm transition-colors">
          <table class="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
            <thead>
              <tr>
                <th
                  v-for="(header, index) in tableHeaders"
                  :key="index"
                  scope="col"
                  class="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider transition-colors"
                >
                  {{ header }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-200 dark:divide-slate-700 transition-colors">
              <tr
                v-for="(row, index) in processedDocuments"
                :key="index"
                class="hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors cursor-pointer"
                @click="viewDocument(row)"
                data-testid="document_table_row"
                :data-id="row.id"
              >
                <td v-for="(value, key) in tableCells(row)" :key="key" class="px-4 py-4 whitespace-nowrap text-sm">
                  <woot-button
                    variant="smooth"
                    color-scheme="alert"
                    icon="delete"
                    v-if="key === 'id'"
                    @click.stop="handleDeleteLocal(value)"
                    aria-label="Delete document"
                    class="transition-colors"
                    data-testid="document_delete_button"
                    :data-id="value"
                  />
                  <div v-else-if="key === 'status'" class="flex items-center" data-testid="document_status_badge">
                    <span
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mr-2"
                      :class="{
                        'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100': value === 'available',
                        'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100': value === 'indexing' || value === 'queuing',
                        'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-100': value === 'unavailable',
                        'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-100': value === 'disabled',
                        'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100': value === 'error',
                      }"
                      v-tooltip.top="value === 'error' ? row.error : null"
                      data-testid="document_status_badge"
                    >
                      <span
                        class="h-2 w-2 mr-1 rounded-full"
                        :class="{
                          'bg-green-500': value === 'available',
                          'bg-yellow-500': value === 'indexing',
                          'bg-slate-500': value === 'unavailable',
                          'bg-slate-500': value === 'disabled',
                          'bg-yellow-300': value === 'queuing',
                          'bg-red-500': value === 'error',
                        }"
                      ></span>
                      {{ getStatusText(value) }}
                    </span>
                  </div>
                  <div v-else-if="key === 'fileName'" class="flex items-center">
                    <span
                      class="inline-flex items-center justify-center h-8 w-8 rounded-md bg-slate-100 dark:bg-slate-700 mr-2"
                      data-testid="document_table_filename_icon"
                    >
                      <fluent-icon :icon="getFileIcon(value)" class="h-4 w-4 text-slate-600 dark:text-slate-400" />
                    </span>
                    <span class="text-slate-900 dark:text-slate-100" data-testid="document_table_filename">{{ value }}</span>
                  </div>
                  <div v-else-if="key === 'source'" class="text-slate-600 dark:text-slate-300" data-testid="document_table_source">
                    {{ toTitleCase(value) }}
                  </div>
                  <div v-else-if="key === 'retrievalCount'" class="text-slate-800 dark:text-slate-200" data-testid="document_table_cell">
                    {{ isLightragEngine ? '—' : value }}
                  </div>
                  <div v-else class="text-slate-800 dark:text-slate-200" data-testid="document_table_cell">
                    {{ value }}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Card view -->
        <div
          v-else
          class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
        >
          <div
            v-for="(doc, index) in processedDocuments"
            :key="index"
            class="bg-white dark:bg-slate-800 rounded-lg shadow-sm overflow-hidden transition-all hover:shadow-md cursor-pointer"
            @click="viewDocument(doc)"
            data-testid="document_card"
            :data-id="doc.id"
          >
            <div class="p-6 flex flex-col h-full">
              <div class="flex items-start justify-between mb-4">
                <div class="flex items-center">
                  <span
                    class="inline-flex items-center justify-center h-10 w-10 rounded-md bg-slate-100 dark:bg-slate-700 mr-3"
                  >
                    <fluent-icon :icon="getFileIcon(doc.fileName)" class="h-5 w-5 text-slate-600 dark:text-slate-400" />
                  </span>
                  <div>
                    <h3 class="text-base font-medium text-slate-900 dark:text-slate-100 line-clamp-1" :title="doc.fileName" data-testid="document_card_filename">
                      {{ doc.fileName }}
                    </h3>
                  </div>
                </div>

                <woot-button
                  variant="smooth"
                  color-scheme="alert"
                  icon="delete"
                  size="small"
                  @click.stop="handleDeleteLocal(doc.id)"
                  aria-label="Delete document"
                  class="transition-colors"
                  data-testid="document_delete_button"
                  :data-id="doc.id"
                />
              </div>

              <div class="flex flex-col gap-2 text-sm flex-grow">
                <div class="flex justify-between items-center">
                  <span class="text-slate-500 dark:text-slate-400">{{ $t('KNOWLEDGE.WORD_COUNT_TITLE') }}:</span>
                  <span class="text-slate-800 dark:text-slate-200 font-medium" data-testid="document_card_word_count">{{ doc.wordCount }}</span>
                </div>
                <div class="flex justify-between items-center">
                  <span class="text-slate-500 dark:text-slate-400">{{ $t('KNOWLEDGE.VIEW_DOCUMENTS.RETRIEVAL_COUNT') }}:</span>
                  <span class="text-slate-800 dark:text-slate-200 font-medium" data-testid="document_card_retrieval_count">{{ isLightragEngine ? '—' : doc.retrievalCount }}</span>
                </div>
                <div class="flex justify-between items-center">
                  <span class="text-slate-500 dark:text-slate-400">{{ $t('KNOWLEDGE.VIEW_DOCUMENTS.UPLOAD_TIME') }}:</span>
                  <span class="text-slate-800 dark:text-slate-200 font-medium" data-testid="document_card_upload_time">{{ doc.uploadTimeFormatted }}</span>
                </div>
              </div>

              <div class="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                <span
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                  :class="{
                    'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100': doc.status === 'available',
                    'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100': doc.status === 'indexing' || doc.status === 'queuing',
                    'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-100': doc.status === 'unavailable',
                    'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100': doc.status === 'error',
                  }"
                  v-tooltip.top="doc.status === 'error' ? doc.error : null"
                  data-testid="document_status_badge"
                >
                  <span
                    class="h-2 w-2 mr-1 rounded-full"
                    :class="{
                      'bg-green-500': doc.status === 'available',
                      'bg-yellow-500': doc.status === 'indexing',
                      'bg-slate-500': doc.status === 'unavailable',
                      'bg-slate-500': doc.status === 'disabled',
                      'bg-yellow-300': doc.status === 'queuing',
                      'bg-red-500': doc.status === 'error',
                    }"
                  ></span>
                  {{ getStatusText(doc.status) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
